import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzModalRef, NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { OrdersFacade } from '../../../+store/order/order.facade';
import { INote, IOrder } from '@appgen-health-crm/api-interfaces';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';

import { NzSelectModule } from 'ng-zorro-antd/select';
import { NoteFacade } from '../../../+store/note/note.facade';
import { ORDER_CANCELLATION_REASONS } from '@appgen-health-crm/data';

@Component({
  selector: 'portals-emr-order-cancelled-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule
  ],
  template: `
    <p class="mb-4 text-neutral-600">
      Cancelling this order will cancel any payment for it.
    </p>
    <form nz-form [formGroup]="FG">
      <nz-form-item class="">
        <nz-form-label nzFor="cancellationReason"
          >Cancellation Reason</nz-form-label
        >
        <nz-form-control nzErrorTip="Please select a cancellation reason">
          <nz-select
            nzRequired
            formControlName="cancellationReason"
            placeholder="Select a reason"
          >
            <nz-option
              *ngFor="let reason of ORDER_CANCELLATION_REASONS"
              [nzValue]="reason.key"
              [nzLabel]="reason.label"
            ></nz-option>
          </nz-select>
        </nz-form-control>
      </nz-form-item>
      <nz-form-item
        *ngIf="FG.get('cancellationReason')?.value === 'Other'"
        class="mt-4"
      >
        <nz-form-label nzFor="otherReason">Please specify</nz-form-label>
        <nz-form-control nzErrorTip="Please enter the reason">
          <input
            nz-input
            nzRequired
            type="text"
            formControlName="otherReason"
            placeholder="Enter other reason"
          />
        </nz-form-control>
      </nz-form-item>
      <nz-form-item>
        <nz-form-control>
          <div class="flex items-center gap-2">
            <span>Send Event (trigger workflows) :</span>
            <input type="checkbox" formControlName="sendEvent" />
          </div>
        </nz-form-control>
      </nz-form-item>
      <div class="flex justify-end gap-2 mt-4">
        <button
          nz-button
          nzType="default"
          tabIndex="0"
          [nzLoading]="orderFacade.loading$ | async"
          (click)="cancel()"
        >
          Cancel
        </button>
        <button
          nz-button
          nzType="default"
          tabIndex="0"
          [nzLoading]="orderFacade.loading$ | async"
          (click)="ok(true)"
        >
          Skip
        </button>
        <button
          nz-button
          nzType="primary"
          tabIndex="0"
          [disabled]="orderFacade.loading$ | async"
          (click)="ok()"
        >
          Save
        </button>
      </div>
    </form>
  `
})
export class OrderedCancelledDialogComponent {
  readonly fb = inject(FormBuilder);
  readonly orderFacade = inject(OrdersFacade);
  readonly modal = inject(NzModalRef);
  readonly data: { order: IOrder } = inject(NZ_MODAL_DATA);
  readonly ORDER_CANCELLATION_REASONS = ORDER_CANCELLATION_REASONS;

  constructor(private noteFacade: NoteFacade) { }

  FG = this.fb.group({
    cancellationReason: ['', [Validators.required]],
    otherReason: [''],
    sendEvent: [true, []]
  });

  ok(skipValidation = false) {
    if (!skipValidation && this.FG.value.cancellationReason === '') {
      this.FG.controls.cancellationReason.markAsDirty();
      this.FG.controls.cancellationReason.updateValueAndValidity();
      return;
    }

    // Other Reason should not be empty when user selects the other option
    if (
      !skipValidation &&
      this.FG.value.cancellationReason === 'Other' &&
      !this.FG.value.otherReason
    ) {
      this.FG.controls.otherReason.markAsDirty();
      this.FG.controls.otherReason.updateValueAndValidity();
      this.FG.controls.otherReason.setErrors({ required: true });
      return;
    }

    const cancellationReason = skipValidation
      ? ''
      : this.FG.value.cancellationReason;
    const otherReason = this.FG.value.otherReason;
    const finalReason =
      cancellationReason === 'Other' ? otherReason : cancellationReason;
    const orderUpdates = {
      status: 'cancelled',
      sendEvent: this.FG.value.sendEvent
    };

    if (finalReason) {
      Object.assign(orderUpdates, {
        cancellationReason: finalReason
      });

      const noteDTO: Partial<INote> = {
        consultType: `Order(#${this.data.order.id})`,
        content: `Order cancelled. Reason: ${finalReason}`,
        customer:
          typeof this.data.order.customer === 'string'
            ? this.data.order.customer
            : this.data.order.customer._id,
        order: this.data.order._id
      };

      this.noteFacade.upsertNote(noteDTO);
    }

    this.orderFacade.updateOrder(this.data.order.id, orderUpdates);

    this.modal.close();
    this.FG.reset();
  }

  cancel() {
    this.modal.close();
    this.orderFacade.updateOrderState(this.data.order.id, {
      status: this.data.order.status
    });
  }
}
